import React from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Autocomplete,
  ListSubheader,
} from '@mui/material';
import { useListCategories } from '../../hooks/useListCategories';
import { Category } from '../../types';

interface CategorySelectorProps {
  onSelect: (category: Category | undefined) => void;
  selectedCategory?: Category | undefined;
  label?: string;
}

export function CategorySelector({ onSelect, selectedCategory, label = 'Category' }: CategorySelectorProps) {
  const { data: categories, isLoading, error } = useListCategories();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error">
        Error loading categories
      </Typography>
    );
  }

  return (
    <Autocomplete
      options={categories || []}
      getOptionLabel={(option) => option.detailedDisplay || ''}
      groupBy={(option) => option.primaryDisplay || ''}
      value={selectedCategory || null}
      onChange={(_, value) => onSelect(value || undefined)}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Search categories..." fullWidth />
      )}
      renderGroup={(params) => (
        <li key={params.group}>
          <ListSubheader 
            sx={{ 
              backgroundColor: 'background.paper',
              fontWeight: 'bold',
              color: 'primary.main',
              pointerEvents: 'none'
            }}
          >
            {params.group}
          </ListSubheader>
          {params.children}
        </li>
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body1">
              {option.detailedDisplay}
            </Typography>
            {option.description && (
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            )}
          </Box>
        </li>
      )}
      clearOnBlur={false}
      autoHighlight
    />
  );
} 